import React, { useEffect, useRef, useState } from 'react';
import { Box, Chip, Paper, Tooltip, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon, Lock as LockIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import temperatureOnlyImg from '../../assets/pour-functions/temperature-only.png';
import concreteMaturityImg from '../../assets/pour-functions/concrete-maturity.png';
import temperatureMatchCuringImg from '../../assets/pour-functions/temperature-match-curing.png';
import externalTempHumidityImg from '../../assets/pour-functions/external-temp-humidity.png';
import onsiteWeatherImg from '../../assets/pour-functions/onsite-weather.png';
import evaporationRateImg from '../../assets/pour-functions/evaporation-rate.png';

export type MonitoringFunctionId =
  | 'Temperature Only'
  | 'Concrete Maturity'
  | 'Temperature Match Curing'
  | 'External Temperature and Humidity'
  | 'Onsite Weather Conditions'
  | 'Calculate Concrete Evaporation Rate';

interface FunctionOption {
  id: MonitoringFunctionId;
  titleKey: string;
  titleFallback: string;
  descKey: string;
  descFallback: string;
  img: string;
}

// Order mirrors the Figma layout: rows read Temperature Only | Maturity,
// TMC | Onsite Weather, Evaporation | External T&H.
export const FUNCTION_OPTIONS: FunctionOption[] = [
  {
    id: 'Temperature Only',
    titleKey: 'pourFunction.temperatureOnly.title',
    titleFallback: 'TEMPERATURE ONLY (Mass Concrete Requirements)',
    descKey: 'pourFunction.temperatureOnly.desc',
    descFallback:
      'Monitor the thermal behavior of concrete to identify when peak temperatures are reached and track temperature differentials throughout the curing process.',
    img: temperatureOnlyImg,
  },
  {
    id: 'Concrete Maturity',
    titleKey: 'pourFunction.maturity.title',
    titleFallback: 'CONCRETE MATURITY (Early Age Strength Estimating)',
    descKey: 'pourFunction.maturity.desc',
    descFallback: 'Accurately determine in-place concrete strength in real time using the maturity method.',
    img: concreteMaturityImg,
  },
  {
    id: 'Temperature Match Curing',
    titleKey: 'pourFunction.tmc.title',
    titleFallback: 'TEMPERATURE MATCH CURING (TMC)',
    descKey: 'pourFunction.tmc.desc',
    descFallback:
      'Match the temperature of in-situ concrete using the TMC method. Temperature data is collected from site and transmitted to the TMC tank, which replicates the same temperature profile in real time.',
    img: temperatureMatchCuringImg,
  },
  {
    id: 'Onsite Weather Conditions',
    titleKey: 'pourFunction.weather.title',
    titleFallback: 'ONSITE WEATHER CONDITIONS',
    descKey: 'pourFunction.weather.desc',
    descFallback:
      'Monitoring environmental conditions to gain an insight into curing performance, mitigate risks such as plastic shrinkage and thermal cracking.',
    img: onsiteWeatherImg,
  },
  {
    id: 'Calculate Concrete Evaporation Rate',
    titleKey: 'pourFunction.evaporation.title',
    titleFallback: 'CALCULATE CONCRETE EVAPORATION RATE',
    descKey: 'pourFunction.evaporation.desc',
    descFallback:
      'Using the environmental conditions and actual concrete temperatures, we can calculate the actual concrete evaporation rate and mitigate the risk of plastic shrinkage, thermal cracking etc.',
    img: evaporationRateImg,
  },
  {
    id: 'External Temperature and Humidity',
    titleKey: 'pourFunction.externalTH.title',
    titleFallback: 'EXTERNAL TEMPERATURE AND HUMIDITY',
    descKey: 'pourFunction.externalTH.desc',
    descFallback: 'Monitor the external temperature and Humidity (Ideal for Curing rooms / cycles).',
    img: externalTempHumidityImg,
  },
];

export const ALL_MONITORING_FUNCTIONS: MonitoringFunctionId[] = FUNCTION_OPTIONS.map(
  (opt) => opt.id,
);

// Selecting any of these auto-includes the listed prerequisites.
export const FUNCTION_DEPENDENCIES: Record<MonitoringFunctionId, MonitoringFunctionId[]> = {
  'Temperature Only': [],
  'Concrete Maturity': ['Temperature Only'],
  'Temperature Match Curing': ['Temperature Only', 'Concrete Maturity'],
  'External Temperature and Humidity': [],
  'Onsite Weather Conditions': [],
  'Calculate Concrete Evaporation Rate': ['Temperature Only'],
};

const computeSelected = (manual: MonitoringFunctionId[]): MonitoringFunctionId[] => {
  const result: MonitoringFunctionId[] = [];
  for (const id of manual) {
    if (!result.includes(id)) result.push(id);
    for (const dep of FUNCTION_DEPENDENCIES[id] || []) {
      if (!result.includes(dep)) result.push(dep);
    }
  }
  return result;
};

export const monitoringFunctionsToString = (ids: MonitoringFunctionId[]): string =>
  ids.join(', ');

const MASS_CONCRETE_MONITORING_FUNCS = new Set<MonitoringFunctionId>([
  'Temperature Match Curing',
  'External Temperature and Humidity',
  'Onsite Weather Conditions',
  'Calculate Concrete Evaporation Rate',
]);

/** Derive the Functionality 1/2/3 code from the monitoring functions list.
 *  1 = Concrete Maturity, 2 = Mass Concrete, 3 = Both.
 *  Mirrors the backend's _derive_functionality logic in pour_service.py. */
export const deriveFunctionalityCode = (ids: MonitoringFunctionId[]): '1' | '2' | '3' => {
  const hasMaturity = ids.includes('Concrete Maturity');
  const hasMassConcrete = ids.some((id) => MASS_CONCRETE_MONITORING_FUNCS.has(id));
  if (hasMaturity && hasMassConcrete) return '3';
  if (hasMaturity) return '1';
  return '2';
};

export const stringToMonitoringFunctions = (value: string | null | undefined): MonitoringFunctionId[] => {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s): s is MonitoringFunctionId =>
      FUNCTION_OPTIONS.some((opt) => opt.id === s),
    );
};

interface PourFunctionSelectorProps {
  value: MonitoringFunctionId[];
  onChange: (next: MonitoringFunctionId[]) => void;
  disabled?: boolean;
  /** Pour-type access (company licensing ∩ per-user grant). Functions that
   *  fail it are greyed out with a lock and can't be added; omit to allow
   *  everything. The backend re-checks on save either way. */
  isAllowed?: (funcId: MonitoringFunctionId) => boolean;
  /** The user's own picks, when the page keeps them (with onPicksChange),
   *  so they outlast this selector: a stepper that shows one step at a time
   *  unmounts it. The page then sets them when it replaces the value
   *  (loading a pour, discarding changes). Omit to keep them here. */
  picks?: MonitoringFunctionId[];
  onPicksChange?: (picks: MonitoringFunctionId[]) => void;
}

const PourFunctionSelector: React.FC<PourFunctionSelectorProps> = ({
  value, onChange, disabled, isAllowed, picks, onPicksChange,
}) => {
  const { t } = useTranslation();

  // Track which entries the user explicitly clicked. On initial load (edit
  // mode) we treat the whole stored selection as "manual" since we can't
  // distinguish dependency-pulled entries from explicit clicks after the
  // fact. The auto-include chip is then driven by user interaction during
  // this session.
  const [ownPicks, setOwnPicks] = useState<MonitoringFunctionId[]>(value);
  const pageKeepsPicks = picks !== undefined;
  const manuallySelected = picks ?? ownPicks;
  // The selection the last click sent to the parent.
  const emitted = useRef<MonitoringFunctionId[] | null>(null);

  useEffect(() => {
    // Resync when the parent replaces the value wholesale (switching pours,
    // discarding changes), not when it hands back the selection a click
    // sent: resyncing then made every dependency a pick of its own, so a
    // second click no longer took back only the first. A page that keeps
    // the picks resyncs them itself.
    if (pageKeepsPicks || value === emitted.current) return;
    setOwnPicks(value);
  }, [value, pageKeepsPicks]);

  const isAutoIncluded = (funcId: MonitoringFunctionId): boolean =>
    value.includes(funcId) && !manuallySelected.includes(funcId);

  const allowedFn = (funcId: MonitoringFunctionId): boolean => !isAllowed || isAllowed(funcId);

  // As in the create wizard (PourCreate toggleFunction), so a second click
  // on a card puts the selection back as it was before the first.
  const toggleFunction = (funcId: MonitoringFunctionId) => {
    if (disabled) return;
    let nextManual: MonitoringFunctionId[];
    if (manuallySelected.includes(funcId)) {
      // Unticking only takes back the user's own pick. When another chosen
      // function still needs it, it stays selected as a dependency and the
      // functions that need it are kept.
      nextManual = manuallySelected.filter((f) => f !== funcId);
    } else {
      // Adding needs pour-type access. Ticking a function another one added
      // as a dependency makes it the user's own pick; the selection itself
      // is unchanged.
      if (!value.includes(funcId) && !allowedFn(funcId)) return;
      nextManual = [...manuallySelected, funcId];
    }
    const next = computeSelected(nextManual);
    if (pageKeepsPicks) onPicksChange?.(nextManual);
    else setOwnPicks(nextManual);
    emitted.current = next;
    onChange(next);
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('pourFunction.helper', 'Select one or multiple functions.')}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {FUNCTION_OPTIONS.map((func) => {
          const selected = value.includes(func.id);
          const autoIncluded = isAutoIncluded(func.id);
          // Already-selected functions stay usable (deselectable) even when
          // not grantable; only adding them is blocked.
          const locked = !selected && !allowedFn(func.id);
          const card = (
            <Paper
              onClick={() => toggleFunction(func.id)}
              sx={{
                p: 2,
                cursor: disabled ? 'default' : locked ? 'not-allowed' : 'pointer',
                border: 2,
                borderColor: selected ? 'primary.main' : 'grey.300',
                backgroundColor: selected
                  ? 'rgba(121,168,42,0.14)'
                  : locked
                    ? 'action.disabledBackground'
                    : 'background.paper',
                boxShadow: selected ? '0 0 0 3px rgba(121,168,42,0.4)' : 'none',
                transition: 'all 0.2s',
                opacity: locked ? 0.55 : disabled ? 0.7 : 1,
                '&:hover': disabled || locked
                  ? undefined
                  : {
                      borderColor: 'primary.main',
                      boxShadow: 2,
                    },
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                position: 'relative',
              }}
            >
              {selected && !autoIncluded && (
                <CheckCircleIcon
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    fontSize: 26,
                    color: 'primary.main',
                    backgroundColor: 'background.paper',
                    borderRadius: '50%',
                  }}
                />
              )}
              {autoIncluded && (
                <Chip
                  label={t('pourFunction.required', 'Required')}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: '0.75rem',
                    height: 20,
                  }}
                />
              )}
              {locked && (
                <Chip
                  icon={<LockIcon sx={{ fontSize: 14 }} />}
                  label={t('pourFunction.notInPlan', 'Not included in your plan')}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'grey.300',
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    height: 20,
                  }}
                />
              )}
              <Box
                component="img"
                src={func.img}
                alt=""
                sx={{
                  width: 88,
                  height: 72,
                  objectFit: 'contain',
                  flexShrink: 0,
                  filter: locked ? 'grayscale(1)' : 'none',
                }}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {t(func.titleKey, func.titleFallback)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85, fontSize: '0.8rem' }}>
                  {t(func.descKey, func.descFallback)}
                </Typography>
              </Box>
            </Paper>
          );
          if (!locked || disabled) return <React.Fragment key={func.id}>{card}</React.Fragment>;
          return (
            <Tooltip
              key={func.id}
              title={t(
                'pourFunction.notInPlanTooltip',
                'This feature is not included in your current subscription. Please contact ConXedge if you would like to enable it.',
              )}
              arrow
            >
              {card}
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default PourFunctionSelector;
